import { createSelector } from 'reselect';

import { StoreState } from '../index';
import { FeatureComparison } from 'app/company/profile/billing/types';

export interface PolicyGrowthArticle {
    title: string;
    description: string;
    imgSrc: string;
    link: string;
}

const getOpDurationLimit = ( { config }: StoreState ): number => {
    return config && config.opDurationLimit
        ? JSON.parse( config.opDurationLimit )
        : 0;
};

const getAdjustmentLimits = ( { config }: StoreState ): { [ key: string ]: number } => {
    return config
        ? {
            businessUserBidAdjustmentLimitPieceWork: config.businessUserBidAdjustmentLimitPieceWork || 250
            , businessUserBidAdjustmentLimitTaskWork: config.businessUserBidAdjustmentLimitTaskWork || 200
            , internalUserBidAdjustmentLimitPieceWork: config.internalUserBidAdjustmentLimitPieceWork || 400
            , internalUserBidAdjustmentLimitTaskWork: config.internalUserBidAdjustmentLimitTaskWork || 400
        }
        : {
            businessUserBidAdjustmentLimitPieceWork: 250
            , businessUserBidAdjustmentLimitTaskWork: 200
            , internalUserBidAdjustmentLimitPieceWork: 400
            , internalUserBidAdjustmentLimitTaskWork: 400
        };
};

const getLeadTimeOptions = ( { config }: StoreState ): number[] => {
    return config?.leadTimeOptions
    ?? [
        0
        , 12
        , 24
        , 36
        , 48
        , 60
        , 72
    ];
};

const getBulkAcceptanceConcurrency = ( { config }: StoreState ): number => config?.bulkAcceptanceConcurrency ?? 2;

const getMaxOperatorsNeeded = ( { config }: StoreState ): number => {
    return config && config.maxOperatorsNeeded
        ? JSON.parse( config.maxOperatorsNeeded )
        : 40;
};

const getFeatureComparisons = ( { config }: StoreState ): FeatureComparison[] => {
    return config.featureComparisons ?? [];
};

const getReadOnlyBillingPageLegacyInvoicingBusinessIds = ( { config }: StoreState ): number[] => {
    return config.readOnlyBillingPageLegacyInvoicingBusinessIds ?? [];
};

const getIsPopoverTrackerOn = ( { config }: StoreState ): boolean => {
    return config.isPopoverTrackerOn ?? true;
};

const getPreNovaAdjustReasons = ( { config }: StoreState ): string[] => {
    return config.preNovaAdjustDownReasons ?? [];
};

const getExemptLateCancellationFeeBusinessIds = ( { config }: StoreState ): number[] => {
    return config.exemptLateCancellationFeeBusinessIds ?? [];
};

const getPolicyGrowthArticle = ( { config }: StoreState ): PolicyGrowthArticle[] => {
    return config.PolicyGrowthArticle ?? [];
};

const getOutstandingInvoiceLength = ( { config }: StoreState ): number => {
    return config.outstandingInvoiceLength ?? 28;
};

const getChartGPTPrompts = ( { config }: StoreState ): string[] => {
    return config.chartGPTPrompts ?? [
        'Show me how many ops I\'ve posted this year by week.'
        , 'Show me Operators Requested vs Operators Accepted for each week last month.'
        , 'Which 10 Operators have worked the most Ops for me?'
    ];
};

const getRosterDefaultDistricts = ( { config }: StoreState ): string[] => {
    return config.rosterDefaultDistricts ?? [];
};

export const selectIsPopoverTrackerOn = createSelector(
    getIsPopoverTrackerOn
    , isPopoverTrackerOn => isPopoverTrackerOn
);

export const selectFeatureComparisons = createSelector(
    getFeatureComparisons
    , featureComparisons => featureComparisons
);

export const selectOpDurationLimit = createSelector(
    getOpDurationLimit
    , opDurationLimit => opDurationLimit
);

export const selectAdjustmentLimits = createSelector(
    getAdjustmentLimits
    , adjustmentLimits => adjustmentLimits
);

export const selectMaxOperatorsNeeded = createSelector(
    getMaxOperatorsNeeded
    , maxOperatorsNeeded => maxOperatorsNeeded
);

export const selectLeadTimeOptions = createSelector(
    getLeadTimeOptions
    , leadTimeOptions => leadTimeOptions
);

const getWfmUserList = ( { config }: StoreState ): number[] => {
    return config?.wfmUsers;
};

export const selectWfmUserList = createSelector(
    getWfmUserList
    , wfmUserList => wfmUserList
);

export const selectReadOnlyBillingPageLegacyInvoicingBusinessIds = createSelector(
    getReadOnlyBillingPageLegacyInvoicingBusinessIds
    , readOnlyBillingPageLegacyInvoicingBusinessIds => readOnlyBillingPageLegacyInvoicingBusinessIds
);

export const selectBulkAcceptanceConcurrency = createSelector(
    getBulkAcceptanceConcurrency
    , bulkAcceptanceConcurrency => bulkAcceptanceConcurrency
);

export const selectPreNovaAdjustDownReasons = createSelector(
    getPreNovaAdjustReasons
    , preNovaAdjustDownReasons => preNovaAdjustDownReasons
);

export const selectExemptLateCancellationFeeBusinessIds = createSelector(
    getExemptLateCancellationFeeBusinessIds
    , exemptLateCancellationFeeBusinessIds => exemptLateCancellationFeeBusinessIds
);

export const selectPolicyGrowthArticle = createSelector(
    getPolicyGrowthArticle
    , policyGrowthArticle => policyGrowthArticle
);

export const selectOutstandingInvoiceLength = createSelector(
    getOutstandingInvoiceLength
    , outstandingInvoiceLength => outstandingInvoiceLength
);

export const selectChartGPTPrompts = createSelector(
    getChartGPTPrompts
    , chartGPTPrompts => chartGPTPrompts
);

export const selectRosterDefaultDistricts = createSelector(
    getRosterDefaultDistricts
    , rosterDefaultDistricts => rosterDefaultDistricts
);
